<template>
  <v-form
    ref="appointment-patient-form"
    data-qa="appointment_patient_form"
    :class="isMobile ? 'px-2' : 'px-6'"
    :disabled="isSavingProfile"
  >
    <v-text-field
      ref="appointment-patient-field-surname"
      outlined
      :label="$t('Фамилия', options)"
      data-qa="appointment_patient_form_surname_input"
      :disabled="disabledFields.surname || isSavingProfile"
      :value="patient.surname"
      :rules="fioValidationRules.surname"
      @input="updatePatient({ field: 'surname', value: $event })"
    />
    <v-text-field
      ref="appointment-patient-field-name"
      outlined
      :label="$t('Имя', options)"
      data-qa="appointment_patient_form_name_input"
      :disabled="disabledFields.name || isSavingProfile"
      :value="patient.name"
      :rules="fioValidationRules.name"
      @input="updatePatient({ field: 'name', value: $event })"
    />
    <div>
      <v-text-field
        ref="appointment-patient-field-patronymic"
        outlined
        :label="$t('Отчество', options)"
        data-qa="appointment_patient_form_patronymic_input"
        class="mb-1"
        hide-details="auto"
        :value="patient.patronymic"
        :rules="patient.hasPatronymic ? fioValidationRules.patronymic : []"
        :disabled="!patient.hasPatronymic || disabledFields.patronymic || isSavingProfile"
        @input="updatePatient({ field: 'patronymic', value: $event })"
      >
        <template #message="{ message }">
          <div
            class="pb-1"
          >
            {{ $t(message, options) }}
          </div>
        </template>
      </v-text-field>
      <div
        data-qa="appointment_patient_form_switch_patronymic"
        class="text-body-2 mb-4 pl-3 d-inline-block"
        :class="disabledFields.patronymic || isSavingProfile ? 'disabled--text' : 'primary--text cursor-pointer'"
        @click="disabledFields.patronymic || isSavingProfile ? undefined : updatePatient({ field: 'hasPatronymic', value: !patient.hasPatronymic })"
      >
        {{ !patient.hasPatronymic ? $t('Есть отчество', options) : $t('Нет отчества', options) }}
      </div>
    </div>
    <v-text-field
      ref="appointment-patient-field-birthday"
      v-mask="birthDayMask"
      outlined
      :label="$t('Дата рождения', options)"
      data-qa="appointment_patient_form_birthday_input"
      :placeholder="$t('ДД.ММ.ГГГГ', options)"
      :disabled="disabledFields.birthDay || isSavingProfile"
      :rules="birthDayValidationRules"
      :value="patient.birthDay"
      :validate-on-blur="patient.birthDay.length < birthDayMask.length"
      :hint="birthDayValidationHint"
      persistent-hint
      @input="updatePatient({ field: 'birthDay', value: $event })"
      @keyup.enter="handleSave"
    />
    <div class="mb-6">
      <v-radio-group
        ref="appointment-patient-field-gender"
        row
        hide-details
        class="mt-0 pt-0"
        :value="patient.gender"
        :disabled="disabledFields.gender || isSavingProfile"
        :rules="genderValidationRules"
        data-qa="appointment_patient_form_radios"
        @change="updatePatient({ field: 'gender', value: $event })"
      >
        <div class="text-caption w-100 mb-3">
          {{ $t('Пол', options) }}
        </div>
        <v-radio
          value="M"
          class="ml-2"
          data-qa="appointment_patient_form_radio_m"
        >
          <template #label>
            <div class="text-body-1">
              {{ $t('Мужской', options) }}
            </div>
          </template>
        </v-radio>
        <v-radio
          value="F"
          class="ml-10"
          data-qa="appointment_patient_form_radio_f"
        >
          <template #label>
            <div class="text-body-1">
              {{ $t('Женский', options) }}
            </div>
          </template>
        </v-radio>
      </v-radio-group>
    </div>
    <v-btn
      color="primary"
      class="w-100 mt-2"
      depressed
      :disabled="isSavingProfile"
      :loading="isSavingProfile"
      @click="handleSave"
    >
      {{ $t('Сохранить', options) }}
    </v-btn>
  </v-form>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { REGEX, scrollToElement } from 'www/utils'
import { getPatient } from 'www/vue_components/common/AppointmentPage/interfaces'
import {
  getReplacedLatinEWithDiaeresis,
  getBirthDayValidationRulesAllSpecialties,
} from 'www/vue_components/common/AppointmentPage/functions'
import {
  useDoctorDataStore,
  useAppointmentDataStore,
} from 'www/vue_components/common/AppointmentPage/stores'
import useFamilyProfilesStore from 'www/vue_components/common/FamilyProfiles/stores/useFamilyProfilesStore'
import { getBirthDayValidationHint } from 'www/vue_components/common/AppointmentPage/functions/getBirthDayValidationRulesAllSpecialties'

export default {
  name: 'AppointmentPatientForm',
  props: {
    initPatient: {
      type: Object,
      default: getPatient,
    },
    disabledFields: {
      type: Object,
      default: () => ({
        surname: false,
        name: false,
        patronymic: false,
        birthDay: false,
        gender: false,
      }),
    },
  },
  data: vm => ({
    isMobile: window.MOBILE_VERSION,
    options: { ns: vm.$i18next.language },
    birthDayMask: '##.##.####',
    patient: getPatient(),
    fioFieldNames: [
      'surname',
      'name',
      'patronymic',
    ],
  }),
  computed: {
    ...mapState(useAppointmentDataStore, [
      'appointmentObjectTypes',
    ]),
    ...mapState(useFamilyProfilesStore, [
      'patientFormRef',
      'patientFormFieldRefs',
      'modalContentEl',
    ]),
    ...mapState(useFamilyProfilesStore, [
      'fioValidationRules',
      'genderValidationRules',
      'isSavingProfile',
    ]),
    ...mapState(useDoctorDataStore, [
      'rangeAgeLimit',
      'rangeAgeLimitAllSpecialities',
    ]),
    birthDayValidationRules() {
      const { required, ageLimit } = getBirthDayValidationRulesAllSpecialties(
        this.rangeAgeLimitAllSpecialities,
        { isServices: this.appointmentObjectTypes.isServices },
      )

      // Последовательность имеет значение
      return [required, ageLimit]
    },
    birthDayValidationHint() {
      return getBirthDayValidationHint(
        this.rangeAgeLimitAllSpecialities,
        null,
        this.appointmentObjectTypes.isServices,
      )
    },
    isValidSurname() {
      return this.fioValidationRules.surname.every(rule => rule(this.patient.surname) === true)
    },
    isValidName() {
      return this.fioValidationRules.name.every(rule => rule(this.patient.name) === true)
    },
    isValidPatronymic() {
      return this.patient.hasPatronymic
        ? this.fioValidationRules.patronymic.every(rule => rule(this.patient.patronymic) === true)
        : true
    },
    isValidBirthDay() {
      return this.birthDayValidationRules.every(rule => rule(this.patient.birthDay) === true)
    },
    isValidGender() {
      return this.genderValidationRules.every(rule => rule(this.patient.gender) === true)
    },
    isValidForm() {
      return this.isValidSurname
        && this.isValidName
        && this.isValidPatronymic
        && this.isValidBirthDay
        && this.isValidGender
    },
  },
  watch: {
    initPatient: {
      handler(data) {
        this.patient = data
      },
      immediate: true,
    },
  },
  mounted() {
    this.updatePatientFormRef(this.$refs['appointment-patient-form'])

    const fieldBirthdayRef = this.$refs['appointment-patient-field-birthday']

    this.updatePatientFormFieldRefs([
      this.$refs['appointment-patient-field-surname'],
      this.$refs['appointment-patient-field-name'],
      this.$refs['appointment-patient-field-patronymic'],
      this.$refs['appointment-patient-field-gender'],
      fieldBirthdayRef,
    ])

    this.$watch('rangeAgeLimit', (newValue, oldValue) => {
      if (
        oldValue
                && newValue.min === oldValue.min
                && newValue.max === oldValue.max
      ) {
        return
      }

      fieldBirthdayRef.validate()
    })
  },
  methods: {
    ...mapActions(useFamilyProfilesStore, [
      'updatePatientFormRef',
      'updatePatientFormFieldRefs',
    ]),
    updatePatient({ field, value }) {
      const formattedValue = this.fioFieldNames.includes(field) && REGEX.latinEWithDiaeresis.test(value)
        ? getReplacedLatinEWithDiaeresis(value)
        : value

      this.$set(this.patient, field, formattedValue)

      if (!this.patient.hasPatronymic) {
        this.patient.patronymic = ''
      }
    },
    handleSave() {
      if (this.isValidForm) {
        this.$emit('appointment-patient-form:save', this.patient)
        return
      }

      this.patientFormRef.validate()

      if (!this.modalContentEl) {
        return
      }

      const invalidFieldRef = this.patientFormFieldRefs.find(({ valid }) => !valid)

      if (invalidFieldRef) {
        scrollToElement(invalidFieldRef.$el, this.modalContentEl)
      }
    },
  },
}
</script>

<style lang="scss">
@import '~www/themes/doctors/common/variables';

.v-radio .v-label {
  color: $ui-kit-text;
}
</style>
